import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock, Send } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuthStore, useCan, useIsPlatform } from '@/store/auth';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge, StatusBadge } from '@/components/ui/Badge';
import { Field, Select, Textarea } from '@/components/ui/Input';
import { ErrorState, PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { formatDateTime, formatRelative } from '@/lib/format';
import { KATEGORI, KEPARAHAN } from './BugReportsPage';
import { cn } from '@/lib/cn';

const STATUS_PILIHAN = [
  ['open', 'Terbuka'],
  ['triaged', 'Ditriase'],
  ['in_progress', 'Dikerjakan'],
  ['resolved', 'Selesai'],
  ['wont_fix', 'Tidak Diperbaiki'],
  ['duplicate', 'Duplikat'],
];

const PRIORITAS = [
  ['', 'Belum ditentukan'],
  ['p0', 'P0 - segera'],
  ['p1', 'P1 - tinggi'],
  ['p2', 'P2 - sedang'],
  ['p3', 'P3 - rendah'],
];

/** Blok teks opsional; tidak dirender sama sekali kalau pelapor mengosongkannya. */
const Bagian = ({ judul, isi }) =>
  isi ? (
    <div>
      <h3 className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
        {judul}
      </h3>
      <p className="mt-1.5 text-sm whitespace-pre-wrap text-[var(--text-secondary)]">{isi}</p>
    </div>
  ) : null;

const Balasan = ({ komentar }) => {
  const penulis = komentar.authorId ?? {};
  return (
    <div
      className={cn(
        'rounded-lg border p-3',
        komentar.isInternal
          ? 'border-warning-200 bg-warning-50 dark:border-warning-500/25 dark:bg-warning-500/10'
          : 'border-[var(--border-subtle)] bg-[var(--surface-sunken)]',
      )}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold text-[var(--text-primary)]">
          {penulis.name ?? 'Pengguna'}
        </span>
        {komentar.isInternal && (
          <Badge tone="warning" size="sm">
            <Lock className="size-3" aria-hidden="true" />
            Catatan internal
          </Badge>
        )}
        <span className="text-xs text-[var(--text-muted)]">
          {formatRelative(komentar.createdAt)}
        </span>
      </div>
      <p className="text-sm whitespace-pre-wrap text-[var(--text-secondary)]">{komentar.body}</p>
    </div>
  );
};

/**
 * Melempar laporan ke tingkat lain.
 *
 * ── Kenapa terpisah dari kontrol triase lainnya ─────────────────────────
 *
 * Memindahkan laporan MENGHILANGKANNYA dari layar orang yang memindahkan.
 * Itu akibat yang jauh lebih besar daripada mengganti status atau prioritas,
 * dan disebutkan apa adanya di sini supaya tidak ditekan tanpa disadari.
 *
 * Alasannya wajib. Yang menerima cuma melihat laporan baru muncul di
 * daftarnya, dan tanpa alasan ia tidak tahu kenapa dianggap tepat
 * menanganinya.
 */
const TINGKAT = [
  ['admin', 'Admin', 'Pertanyaan pemakaian, tagihan, atau akun'],
  ['dev', 'Dev', 'Perlu perbaikan di kode atau skrip'],
  ['root', 'Root', 'Menyangkut infrastruktur atau data lintas pelanggan'],
];

const PanelLempar = ({ laporan }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [tujuan, setTujuan] = useState('');
  const [alasan, setAlasan] = useState('');

  const lempar = useMutation({
    mutationFn: () => api.post(`/bug-reports/${laporan.id}/lempar`, { tujuan, alasan }),
    onSuccess: () => {
      toast.success(`Laporan dipindahkan ke ${tujuan}`, {
        description: 'Laporan ini tidak lagi muncul di daftar Anda.',
      });
      queryClient.invalidateQueries({ queryKey: ['bug-reports'] });
      queryClient.invalidateQueries({ queryKey: ['bug-report', laporan.id] });
    },
    onError: (err) =>
      toast.error('Gagal memindahkan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const sekarang = laporan.tujuan ?? 'admin';
  const siap = tujuan && tujuan !== sekarang && alasan.trim().length >= 5;

  return (
    <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-3.5">
      <p className="text-sm font-bold text-[var(--text-primary)]">Pindahkan ke tingkat lain</p>
      <p className="mt-0.5 text-xs leading-relaxed text-[var(--text-muted)]">
        Sekarang di <b className="font-semibold">{sekarang}</b>. Setelah dipindahkan, laporan ini
        tidak lagi muncul di daftar Anda.
      </p>

      <div className="mt-3 space-y-2">
        <Select
          value={tujuan}
          onChange={(e) => setTujuan(e.target.value)}
          aria-label="Tingkat tujuan"
        >
          <option value="">Pilih tingkat...</option>
          {TINGKAT.filter(([k]) => k !== sekarang).map(([k, nama, ket]) => (
            <option key={k} value={k}>
              {nama} - {ket}
            </option>
          ))}
        </Select>

        <Textarea
          rows={2}
          value={alasan}
          onChange={(e) => setAlasan(e.target.value)}
          placeholder="Kenapa tingkat itu yang tepat menanganinya?"
          aria-label="Alasan pemindahan"
        />

        <Button
          size="sm"
          variant="secondary"
          className="w-full"
          disabled={!siap}
          loading={lempar.isPending}
          onClick={() => lempar.mutate()}
        >
          Pindahkan
        </Button>
      </div>

      {laporan.riwayatTujuan?.length > 0 && (
        <ul className="mt-3 space-y-1 border-t border-[var(--border-subtle)] pt-2">
          {laporan.riwayatTujuan.map((r, i) => (
            <li key={i} className="text-[11px] leading-relaxed text-[var(--text-muted)]">
              {r.dari} &rarr; {r.ke}
              {r.alasan ? `: ${r.alasan}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/**
 * Rincian satu laporan bug beserta utasan percakapannya.
 *
 * Panel triase di kanan hanya muncul untuk admin. Menyembunyikannya dari
 * anggota biasa bukan pengamanannya - server menolak PATCH dari peran di
 * bawah admin - tapi memunculkan kontrol yang pasti gagal hanya membingungkan.
 */
export const BugReportDetailPage = () => {
  const { id } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  // Kedua hook dipanggil tanpa syarat, lalu hasilnya dipakai terpisah.
  // Menulis `useCan(...) || useIsPlatform(...)` akan melewati hook kedua
  // begitu yang pertama bernilai benar, dan urutan hook yang berubah antar
  // render adalah galat React yang baru muncul jauh dari penyebabnya.
  //
  // Keduanya dipisah karena haknya memang berbeda: admin ruang kerja boleh
  // MEMBACA seluruh laporan ruang kerjanya, tetapi triase dan catatan
  // internal hanya untuk tim Crawel. Admin ruang kerja seringkali justru si
  // pelapornya sendiri, dan catatan yang ditulis tentang laporannya tidak
  // boleh terbaca olehnya.
  const adminRuangKerja = useCan('admin');
  const bolehTriase = useIsPlatform('admin', 'dev');
  const user = useAuthStore((s) => s.user);

  const [balasan, setBalasan] = useState('');
  const [internal, setInternal] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['bug-reports', id],
    queryFn: () => api.get(`/bug-reports/${id}`),
  });

  const laporan = data?.data;

  const triase = useMutation({
    mutationFn: (patch) => api.patch(`/bug-reports/${id}`, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bug-reports'] });
      toast.success('Laporan diperbarui');
    },
    onError: (err) =>
      toast.error('Gagal memperbarui', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  const kirimBalasan = useMutation({
    mutationFn: () => api.post(`/bug-reports/${id}/comments`, { body: balasan, isInternal: internal }),
    onSuccess: () => {
      setBalasan('');
      setInternal(false);
      queryClient.invalidateQueries({ queryKey: ['bug-reports', id] });
    },
    onError: (err) =>
      toast.error('Balasan gagal dikirim', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (isLoading) return <PageLoader label="Memuat laporan…" />;
  if (isError) {
    return (
      <ErrorState message={error?.message} requestId={error?.requestId} onRetry={refetch} />
    );
  }

  const pelapor = laporan.reporterId ?? {};
  const parah = KEPARAHAN[laporan.severity] ?? KEPARAHAN.medium;
  const milikSendiri = String(pelapor.id ?? pelapor._id ?? '') === String(user?.id ?? '');

  return (
    <div className="space-y-4">
      <Link
        to="/lapor-bug"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--text-muted)] transition-colors hover:text-[var(--text-primary)]"
      >
        <ArrowLeft className="size-4" aria-hidden="true" />
        Semua laporan
      </Link>

      <div className="grid gap-4 lg:grid-cols-[1fr_300px]">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader
              title={
                <span className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-xs font-bold text-[var(--text-muted)]">
                    {laporan.reference}
                  </span>
                  <StatusBadge status={laporan.status} size="sm" />
                  <Badge tone={parah.tone} size="sm">
                    {parah.label}
                  </Badge>
                </span>
              }
            />
            <CardBody className="space-y-5">
              <h1 className="text-lg font-bold text-[var(--text-primary)]">{laporan.title}</h1>

              <p className="text-xs text-[var(--text-muted)]">
                Dilaporkan {pelapor.name ? `oleh ${pelapor.name} ` : ''}
                {formatRelative(laporan.createdAt)} · {KATEGORI[laporan.category] ?? laporan.category}
              </p>

              <Bagian judul="Apa yang terjadi" isi={laporan.description} />
              <Bagian judul="Langkah untuk mengulangi" isi={laporan.stepsToReproduce} />
              <Bagian judul="Yang diharapkan" isi={laporan.expectedResult} />
              <Bagian judul="Yang terjadi" isi={laporan.actualResult} />
              <Bagian judul="Catatan penyelesaian" isi={laporan.resolution} />

              {/* Potret lingkungan diambil server; berguna saat masalahnya
                  hanya muncul di peramban atau versi tertentu. */}
              {(laporan.environment?.userAgent || laporan.environment?.pageUrl) && (
                <div className="rounded-lg bg-[var(--surface-sunken)] p-3">
                  <h3 className="text-xs font-bold tracking-wide text-[var(--text-muted)] uppercase">
                    Lingkungan
                  </h3>
                  <dl className="mt-2 space-y-1 font-mono text-[11px] text-[var(--text-secondary)]">
                    {laporan.environment.pageUrl && (
                      <div className="flex gap-2">
                        <dt className="shrink-0 text-[var(--text-muted)]">Halaman</dt>
                        <dd className="min-w-0 break-all">{laporan.environment.pageUrl}</dd>
                      </div>
                    )}
                    {laporan.environment.userAgent && (
                      <div className="flex gap-2">
                        <dt className="shrink-0 text-[var(--text-muted)]">Peramban</dt>
                        <dd className="min-w-0 break-all">{laporan.environment.userAgent}</dd>
                      </div>
                    )}
                    {laporan.environment.appVersion && (
                      <div className="flex gap-2">
                        <dt className="shrink-0 text-[var(--text-muted)]">Versi</dt>
                        <dd>{laporan.environment.appVersion}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader
              title="Percakapan"
              description={`${laporan.comments?.length ?? 0} balasan`}
            />
            <CardBody className="space-y-3">
              {(laporan.comments ?? []).length === 0 ? (
                <p className="py-2 text-sm text-[var(--text-muted)]">
                  Belum ada balasan. Tambahkan keterangan kalau ada informasi baru.
                </p>
              ) : (
                laporan.comments.map((k) => <Balasan key={k._id ?? k.id} komentar={k} />)
              )}

              {(bolehTriase || adminRuangKerja || milikSendiri) && (
                <div className="space-y-2 pt-1">
                  <Textarea
                    rows={3}
                    value={balasan}
                    onChange={(e) => setBalasan(e.target.value)}
                    placeholder="Tulis balasan…"
                  />
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {bolehTriase && (
                      <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
                        <input
                          type="checkbox"
                          checked={internal}
                          onChange={(e) => setInternal(e.target.checked)}
                          className="size-3.5 rounded border-[var(--border-default)]"
                        />
                        Catatan internal - pelapor tidak melihat ini
                      </label>
                    )}
                    <Button
                      icon={Send}
                      size="sm"
                      className="ml-auto"
                      disabled={!balasan.trim()}
                      loading={kirimBalasan.isPending}
                      onClick={() => kirimBalasan.mutate()}
                    >
                      Kirim
                    </Button>
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {bolehTriase && (
          <Card className="h-fit">
            <CardHeader
              title="Triase"
              description="Hanya tim Crawel yang melihat panel ini."
            />
            <CardBody className="space-y-4">
              <PanelLempar laporan={laporan} />

              <Field label="Status">
                {(props) => (
                  <Select
                    {...props}
                    value={laporan.status}
                    onChange={(e) => triase.mutate({ status: e.target.value })}
                    disabled={triase.isPending}
                  >
                    {STATUS_PILIHAN.map(([nilai, label]) => (
                      <option key={nilai} value={nilai}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              <Field label="Prioritas">
                {(props) => (
                  <Select
                    {...props}
                    value={laporan.priority ?? ''}
                    onChange={(e) => triase.mutate({ priority: e.target.value || null })}
                    disabled={triase.isPending}
                  >
                    {PRIORITAS.map(([nilai, label]) => (
                      <option key={nilai || 'kosong'} value={nilai}>
                        {label}
                      </option>
                    ))}
                  </Select>
                )}
              </Field>

              {laporan.resolvedAt && (
                <p className="text-xs text-[var(--text-muted)]">
                  Ditutup {formatDateTime(laporan.resolvedAt)}
                </p>
              )}
            </CardBody>
          </Card>
        )}
      </div>
    </div>
  );
};
